/**
 * Suppress the browser's built-in touch gestures so they don't fight the
 * sketches: pinch-zoom, double-tap zoom, pull-to-refresh, overscroll glow,
 * long-press context menu and text selection.
 *
 * Returns a disposer.
 */
export function installGestureGuards(): () => void {
  const prevent = (e: Event) => e.preventDefault();

  // Block multi-finger and double-tap zoom (Safari-specific gesture events
  // plus the generic touch path). NOTE: we deliberately do NOT blanket-cancel
  // `touchmove` — that would kill scrolling in the menu. Per-element
  // `touch-action` (none on the canvas, pan-y on the menu) handles scroll/zoom.
  const guards: Array<[EventTarget, string, EventListener, AddEventListenerOptions?]> = [
    [document, 'gesturestart', prevent],
    [document, 'gesturechange', prevent],
    [document, 'gestureend', prevent],
    [document, 'contextmenu', prevent],
  ];

  for (const [t, type, fn, opts] of guards) {
    t.addEventListener(type, fn, opts);
  }

  // Double-tap-to-zoom: swallow a second touchend that lands within 300ms.
  let lastTouchEnd = 0;
  const onTouchEnd = (e: Event) => {
    const now = performance.now();
    if (now - lastTouchEnd < 300) e.preventDefault();
    lastTouchEnd = now;
  };
  document.addEventListener('touchend', onTouchEnd, { passive: false });

  return () => {
    for (const [t, type, fn, opts] of guards) {
      t.removeEventListener(type, fn, opts as EventListenerOptions);
    }
    document.removeEventListener('touchend', onTouchEnd);
  };
}
